import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { renderHTML } from '@agility/nextjs';
import Link from 'next/link';
import { KitButton } from 'components/common/cms-kit';
import { useSession } from 'next-auth/react';
import { useTheme } from '@mui/material/styles';
import { isUserSubscriber } from '../../services/subscriptionUtils';

const Container = styled('div')(({ $videoLocation }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  '@media screen and (min-width: 993px)': {
    position: 'relative',
    justifyContent: $videoLocation === 'left' ? 'right' : 'left',
    flexDirection: $videoLocation === 'left' ? 'row-reverse' : 'row',
    minHeight: '420px',
  },
  '@media screen and (max-width: 992px)': {
    padding: '0 30px',
    height: '620px',
  },
  '@media screen and (max-width: 602px)': {
    padding: '0 10px',
    height: '500px',
  },
}));

const EmptyContainer = styled('div')(({ $videoLocation }) => ({
  flexGrow: 1,
  flexShrink: 0,
  flex: 1,
  verticalAlign: 'top',
  '@media screen and (min-width: 993px)': {
    padding: '60px 10px',
    flexDirection: $videoLocation === 'left' ? 'row-reverse' : 'row',
    maxWidth: $videoLocation === 'center' ? '20%' : '50%',
  },
  '@media screen and (max-width: 992px)': {
    display: 'none',
  },
}));

const FloatingButton = styled('div')(({ $videoLocation }) => ({
  verticalAlign: 'top',
  padding: '60px 10px',
  position: 'absolute',
  top: '56%',
  left: $videoLocation === 'left' ? '62%' : '35%',
  '@media screen and (max-width: 992px)': {
    display: 'none',
  },
}));

const MobileFloatingButton = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '330px 10px 0 10px',
  '@media screen and (min-width: 992px)': {
    display: 'none',
  },
});

const VideoContainer = styled('div')({
  '.video': {
    aspectRatio: '16 / 9',
  },
  '@media screen and (min-width: 993px)': {
    position: 'absolute',
    width: '550px',
    height: 'auto',
    top: '15%',
    right: '40%',
    marginRight: '2%',
  },
  '@media screen and (max-width: 992px)': {
    position: 'absolute',
    width: '95%',
    height: 'auto',
    top: '135px',
    right: '3%',
  },
});

const EmbedContainer = styled('div')({
  '@media screen and (min-width: 993px)': {
    margin: '0 0 50px 0',
    width: '95%',
  },
  '@media screen and (max-width: 992px)': {
    width: '110%',
  },
});

const TitleContainer = styled('div')(({ $videoLocation }) => ({
  '@media screen and (min-width: 993px)': {
    left: $videoLocation === 'left' ? '62.45%' : '1%',
    position: 'absolute',
    top: '18%',
    width: '285px',
  },
  '@media screen and (max-width: 992px)': {
    position: 'absolute',
    top: '35px',
    left: '1%',
    width: '95%',
    height: '100px',
  },
}));

const Title = styled('div')(({ $videoLocation }) => ({
  fontWeight: 600,
  textTransform: 'uppercase',
  '@media screen and (min-width: 993px)': {
    textAlign: $videoLocation === 'left' ? 'left' : 'right',
    fontSize: '3.375rem',
    lineHeight: '2.688rem',
  },
  '@media screen and (max-width: 992px)': {
    fontSize: '2rem',
    lineHeight: '2.25rem',
    textAlign: 'center',
  },
}));

const ActionVideoWithButton = ({ module }) => {
  const { fields } = module;
  const theme = useTheme();

  const { data: session } = useSession();
  const [buttonText, setButtonText] = useState(fields?.buttonText);

  useEffect(() => {
    if (session) {
      if (
        isUserSubscriber(session, 'wms') &&
        fields.buttonLabelforSubscribedUser
      ) {
        setButtonText(fields.buttonLabelforSubscribedUser);
      }
    }
  }, [session]);

  const renderButton = (extraProps = {}) => (
    <KitButton
      component={Link}
      href={fields?.buttonUrl?.href}
      round
      size="lg"
      style={{
        backgroundColor: theme.palette.primary.light2,
        color: theme.palette.primary.contrastText,
      }}
      {...extraProps}
      target={fields?.buttonUrl?.target}
      rel="noopener noreferrer"
    >
      {buttonText}
    </KitButton>
  );

  return (
    <React.Fragment>
      <Container $videoLocation={fields.videoLocation}>
        <EmptyContainer $videoLocation={fields.videoLocation}></EmptyContainer>
        <VideoContainer>
          {fields.videoEmbed && (
            <EmbedContainer
              className="prose max-w-full mx-auto"
              dangerouslySetInnerHTML={renderHTML(fields.videoEmbed)}
            />
          )}
        </VideoContainer>
        <TitleContainer $videoLocation={fields.videoLocation}>
          <Title $videoLocation={fields.videoLocation}>{fields.title}</Title>
        </TitleContainer>
        {((!session && fields?.showButton === 'true') || session) && (
          <>
            <FloatingButton $videoLocation={fields.videoLocation}>
              {renderButton()}
            </FloatingButton>
            <MobileFloatingButton>
              {renderButton({ size: undefined })}
            </MobileFloatingButton>
          </>
        )}
      </Container>
    </React.Fragment>
  );
};

export default ActionVideoWithButton;
